import {useState, useRef, useEffect} from 'react';
import styled from 'styled-components';
import {Button, EMPHASIS} from '@forecast-it/design-system';
import {LabelledButton} from '../../tickets/components/LabelledButton';
import {raisedSurfaceShadow} from '../../../styles/elevation';
import {MERGE_TAGS, type MergeTag} from '../utils/insertMergeTag';

interface Props {
	onSelect: (tag: MergeTag) => void;
	disabled?: boolean;
}

const Container = styled.div`
	position: relative;
	display: inline-block;
`;
const TokenGlyph = styled.span`
	font-family: monospace;
	color: #d97706;
`;
const Popover = styled.div`
	position: absolute;
	left: 0;
	top: 100%;
	z-index: 10;
	margin-top: 4px;
	width: 14rem;
	border-radius: 8px;
	border: 1px solid #e2e8f0;
	background: #ffffff;
	padding: 4px 0;
	${raisedSurfaceShadow}
`;
const OptionLabel = styled.span`
	font-weight: 500;
	color: #334155;
`;
const OptionToken = styled.span`
	font-family: monospace;
	font-size: 12px;
	line-height: 16px;
	color: #94a3b8;
`;

export function MergeTagButton({onSelect, disabled}: Props) {
	const [open, setOpen] = useState(false);
	const containerRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		if (!open) return;
		function handleClickOutside(e: MouseEvent) {
			if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
				setOpen(false);
			}
		}
		document.addEventListener('mousedown', handleClickOutside);
		return () => document.removeEventListener('mousedown', handleClickOutside);
	}, [open]);

	return (
		<Container ref={containerRef}>
			<LabelledButton
				emphasis={EMPHASIS.LOW}
				disabled={disabled}
				onPress={() => setOpen(!open)}
				label="Insert dynamic field"
			>
				<TokenGlyph>{'{ }'}</TokenGlyph>
				<span>Fields</span>
			</LabelledButton>

			{open && (
				<Popover>
					{MERGE_TAGS.map(tag => (
						<Button
							key={tag.token}
							emphasis={EMPHASIS.LOW}
							onPress={() => {
								onSelect(tag);
								setOpen(false);
							}}
						>
							<OptionLabel>{tag.label}</OptionLabel>
							<OptionToken>[{tag.token}]</OptionToken>
						</Button>
					))}
				</Popover>
			)}
		</Container>
	);
}
